/**
 * The second sign-in step: authenticator code, passkey or recovery code — or, when the ISP requires two-factor and the
 * person has none yet, its setup (QR code → first code → recovery codes) before the session starts.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { KeyRound, Loader2, ShieldCheck } from 'lucide-react';
import { startAuthentication } from '@simplewebauthn/browser';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { apiError } from '@/shared/api/client';
import { useT } from '@/i18n';

export interface MfaChallenge { mfaToken: string; mfaRequired?: boolean; mfaEnrollRequired?: boolean; methods?: string[] }

export function RecoveryCodes({ codes }: { codes: string[] }) {
  const t = useT();
  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">{t('mfa.recovery_keep')}</p>
      <div className="grid grid-cols-2 gap-1.5 rounded-lg border bg-muted/40 p-3 font-mono text-sm">{codes.map((c) => <span key={c}>{c}</span>)}</div>
      <Button type="button" variant="outline" size="sm" onClick={() => { void navigator.clipboard?.writeText(codes.join('\n')).catch(() => undefined); }}>{t('mfa.copy_codes')}</Button>
    </div>
  );
}

export function MfaStep({ challenge, onDone, onCancel }: { challenge: MfaChallenge; onDone: (data: any) => void; onCancel: () => void }) {
  const t = useT();
  const [code, setCode] = useState(''); const [recovery, setRecovery] = useState(false);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  const [setup, setSetup] = useState<{ qr: string; secret: string } | null>(null);
  const [codes, setCodes] = useState<{ list: string[]; session: any } | null>(null);
  const enroll = !!challenge.mfaEnrollRequired;
  useEffect(() => { if (enroll) axios.post('/api/core/auth/mfa/enroll/start', { mfaToken: challenge.mfaToken }).then((r) => setSetup(r.data)).catch((e) => setErr(apiError(e))); }, []); // eslint-disable-line
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      if (enroll) { const { data } = await axios.post('/api/core/auth/mfa/enroll/confirm', { mfaToken: challenge.mfaToken, code }); setCodes({ list: data.recoveryCodes, session: data.session }); }
      else onDone((await axios.post('/api/core/auth/mfa', { mfaToken: challenge.mfaToken, ...(recovery ? { recoveryCode: code } : { code }) })).data);
    } catch (e2) { setErr(apiError(e2)); } finally { setBusy(false); }
  };
  const passkey = async () => {
    setBusy(true); setErr(null);
    try {
      const { data: options } = await axios.post('/api/core/auth/mfa/passkey-options', { mfaToken: challenge.mfaToken });
      const assertion = await startAuthentication({ optionsJSON: options });
      onDone((await axios.post('/api/core/auth/mfa', { mfaToken: challenge.mfaToken, passkey: assertion })).data);
    } catch (e2) { setErr(apiError(e2)); } finally { setBusy(false); }
  };
  if (codes) return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">{t('mfa.ready')}</h2>
      <RecoveryCodes codes={codes.list} />
      <Button className="h-11 w-full" onClick={() => onDone(codes.session)}>{t('mfa.continue')}</Button>
    </div>
  );
  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="flex items-center gap-2"><ShieldCheck className="size-5 text-primary" /><h2 className="text-xl font-bold">{t(enroll ? 'mfa.setup_title' : 'mfa.title')}</h2></div>
      <p className="text-sm text-muted-foreground">{t(enroll ? 'mfa.setup_sub' : recovery ? 'mfa.recovery_sub' : 'mfa.sub')}</p>
      {enroll && setup && <div className="space-y-2 text-center"><img src={setup.qr} alt={t('mfa.qr')} className="mx-auto size-44 rounded-lg border bg-white p-1" /><div className="font-mono text-xs text-muted-foreground">{setup.secret}</div></div>}
      <Input autoFocus inputMode={recovery ? 'text' : 'numeric'} autoComplete="one-time-code" placeholder={recovery ? 'xxxxx-xxxxx' : '123456'} value={code} onChange={(e) => setCode(e.target.value)} className="h-11 text-center text-lg tracking-widest" />
      {err && <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-600">{err}</div>}
      <Button type="submit" className="h-11 w-full" disabled={busy || !code.trim()}>{busy && <Loader2 className="size-4 animate-spin" />}{t(enroll ? 'mfa.turn_on' : 'mfa.verify')}</Button>
      {!enroll && challenge.methods?.includes('webauthn') && <Button type="button" variant="outline" className="h-11 w-full" onClick={passkey} disabled={busy}><KeyRound className="size-4" />{t('mfa.use_passkey')}</Button>}
      <div className="flex justify-between text-sm">
        {!enroll && challenge.methods?.includes('recovery') ? <button type="button" className="text-primary" onClick={() => { setRecovery(!recovery); setCode(''); }}>{t(recovery ? 'mfa.use_code' : 'mfa.use_recovery')}</button> : <span />}
        <button type="button" className="text-muted-foreground" onClick={onCancel}>{t('mfa.back')}</button>
      </div>
    </form>
  );
}
